import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useSeason } from "@/lib/holiday";

export const Snow = () => {
  const { season } = useSeason();
  const ref = useRef(null);
  useEffect(() => {
    if (!season || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const c = ref.current;
    const ctx = c.getContext("2d");
    const gold = season === "newyear";
    let w, h, id;
    const resize = () => { w = c.width = window.innerWidth; h = c.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const n = w < 768 ? 45 : 90;
    const flakes = Array.from({ length: n }, () => ({ x: Math.random() * w, y: Math.random() * h, r: 1 + Math.random() * 2.6, s: 0.4 + Math.random() * 1.1, d: Math.random() * Math.PI * 2 }));
    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.s; f.d += 0.01; f.x += Math.sin(f.d) * 0.5;
        if (f.y > h) { f.y = -5; f.x = Math.random() * w; }
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fillStyle = gold ? `rgba(227,203,148,${0.35 + f.r / 6})` : `rgba(255,255,255,${0.3 + f.r / 6})`;
        ctx.fill();
      }
      id = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(id); window.removeEventListener("resize", resize); };
  }, [season]);
  if (!season) return null;
  return <canvas ref={ref} data-testid="holiday-snow" className="fixed inset-0 z-[45] pointer-events-none" />;
};

const FLAKES = Array.from({ length: 28 }, (_, i) => ({
  x: (i * 37) % 100, s: 2 + ((i * 7) % 4), d: 3.5 + ((i * 13) % 30) / 10, delay: ((i * 11) % 25) / 10, drift: ((i % 5) - 2) * 12,
}));

export const LoaderSnow = () => (
  <div data-testid="intro-snow" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] pointer-events-none overflow-hidden"
    style={{ maskImage: "radial-gradient(circle, #000 45%, transparent 72%)", WebkitMaskImage: "radial-gradient(circle, #000 45%, transparent 72%)" }}>
    {FLAKES.map((f, i) => (
      <motion.span key={i} className="absolute top-0 rounded-full bg-white" style={{ left: `${f.x}%`, width: f.s, height: f.s, opacity: 0.8, filter: "blur(0.4px)" }}
        initial={{ y: -20, x: 0 }} animate={{ y: 360, x: f.drift }} transition={{ duration: f.d, delay: f.delay, repeat: Infinity, ease: "linear" }} />
    ))}
  </div>
);

export const SantaHat = ({ testId = "santa-hat", delay = 0.4 }) => (
  <motion.svg data-testid={testId} viewBox="0 0 64 52" className="absolute pointer-events-none drop-shadow-[0_3px_4px_rgba(0,0,0,0.5)]"
    style={{ left: "24%", top: "9%", width: "27%", transformOrigin: "40% 85%" }}
    initial={{ y: "-180%", rotate: -40, opacity: 0 }} animate={{ y: 0, rotate: -16, opacity: 1 }}
    transition={{ type: "spring", stiffness: 120, damping: 11, delay }}>
    <defs>
      <linearGradient id={`${testId}-g`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#e0243f" /><stop offset="1" stopColor="#8f0a1f" />
      </linearGradient>
    </defs>
    <path d="M7 40 C12 20 28 5 48 7 C54 8 58 14 57 22 C52 17 47 17 44 22 C42 27 44 33 46 38 Z" fill={`url(#${testId}-g)`} />
    <circle cx="57" cy="24" r="5.5" fill="#fbf7ef" />
    <rect x="3" y="36" width="47" height="11" rx="5.5" fill="#fbf7ef" />
    <path d="M7 41.5 h39" stroke="#e7dfcf" strokeWidth="1.2" strokeDasharray="1.5 3" strokeLinecap="round" />
  </motion.svg>
);

const BAUBLES = ["#c8102e", "#c9a55c", "#0f5132", "#e3cb94", "#c8102e", "#c9a55c", "#0f5132"];

export const Garland = () => {
  const { active } = useSeason();
  if (!active) return null;
  return (
    <div data-testid="holiday-garland" className="relative h-0 pointer-events-none">
      <svg className="absolute top-0 left-0 w-full h-4" preserveAspectRatio="none" viewBox="0 0 100 10">
        <path d="M0 0 Q 12.5 8 25 0 T 50 0 T 75 0 T 100 0" stroke="#1f4d2e" strokeWidth="1.6" fill="none" />
      </svg>
      <div className="absolute top-0 inset-x-0 flex justify-around">
        {BAUBLES.map((c, i) => (
          <motion.div key={i} className="origin-top flex flex-col items-center" style={{ marginTop: i % 2 ? 2 : 6 }}
            animate={{ rotate: [-6, 6, -6] }} transition={{ duration: 3 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}>
            <span className="w-px h-3 lg:h-5 bg-[#c9a55c]/70" />
            <span className="h-3 w-3 lg:h-4 lg:w-4 rounded-full shadow-[0_0_12px_rgba(201,165,92,0.6)]" style={{ background: `radial-gradient(circle at 30% 30%, #fff8, ${c} 45%)` }} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};
